// Anonymous local storage for game state + streaks (Wordle style).
// v3 stores per-case-id state (each date × discipline is its own case) so
// switching disciplines does not destroy in-progress play on another one.
const KEY_STATE_PREFIX = "dxdaily:state:v3:";
const KEY_STATS = "dxdaily:stats";
const KEY_DISCIPLINE = "dxdaily:discipline";
const KEY_STATE_LEGACY_LIST = ["dxdaily:state", "dxdaily:state:v2"];

// Best-effort one-time cleanup of legacy pre-v3 saved state.
try {
  if (typeof window !== "undefined" && window.localStorage) {
    KEY_STATE_LEGACY_LIST.forEach((k) => window.localStorage.removeItem(k));
  }
} catch {
  /* ignore */
}

// Small stable hash so we can detect a regenerated case with the same case_id.
export const caseFingerprint = (caseData) => {
  if (!caseData) return "";
  const src = `${caseData.case_id}|${caseData.vignette}|${(caseData.diagnosis_options || []).join("|")}`;
  let h = 0;
  for (let i = 0; i < src.length; i++) {
    h = (h * 31 + src.charCodeAt(i)) | 0;
  }
  return String(h);
};

export const loadState = (caseId, fingerprint) => {
  if (!caseId) return null;
  try {
    const raw = localStorage.getItem(KEY_STATE_PREFIX + caseId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (fingerprint && parsed?.fingerprint !== fingerprint) {
      localStorage.removeItem(KEY_STATE_PREFIX + caseId);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const saveState = (caseId, state) => {
  if (!caseId) return;
  try {
    localStorage.setItem(KEY_STATE_PREFIX + caseId, JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
};

// -------- Selected discipline (persistent across visits) --------
export const loadDiscipline = () => {
  try {
    return localStorage.getItem(KEY_DISCIPLINE) || null;
  } catch {
    return null;
  }
};

export const saveDiscipline = (name) => {
  try {
    if (name) localStorage.setItem(KEY_DISCIPLINE, name);
  } catch {
    /* ignore */
  }
};

// -------- Streak stats (one global streak, not per discipline) --------
export const loadStats = () => {
  try {
    const raw = localStorage.getItem(KEY_STATS);
    if (!raw) return { played: 0, wins: 0, current_streak: 0, max_streak: 0, last_date: null };
    return JSON.parse(raw);
  } catch {
    return { played: 0, wins: 0, current_streak: 0, max_streak: 0, last_date: null };
  }
};

export const saveStats = (stats) => {
  try {
    localStorage.setItem(KEY_STATS, JSON.stringify(stats));
  } catch {
    /* ignore quota */
  }
};

export const updateStatsAfterGame = (won, date) => {
  const s = loadStats();
  if (s.last_date === date) return s; // one-per-day, first submission counts
  const played = s.played + 1;
  let current_streak = s.current_streak;
  if (won) {
    if (s.last_date) {
      const prev = new Date(s.last_date + "T00:00:00Z").getTime();
      const cur = new Date(date + "T00:00:00Z").getTime();
      const diffDays = Math.round((cur - prev) / 86400000);
      current_streak = diffDays === 1 ? s.current_streak + 1 : 1;
    } else {
      current_streak = 1;
    }
  } else {
    current_streak = 0;
  }
  const next = {
    played,
    wins: s.wins + (won ? 1 : 0),
    current_streak,
    max_streak: Math.max(s.max_streak, current_streak),
    last_date: date,
  };
  saveStats(next);
  return next;
};

export const buildShareGrid = ({ rows, won, date, streak, specialty }) => {
  const grid = rows
    .map((r) => (r.relevant === true ? "🟩" : r.relevant === false ? "🟥" : "⬜"))
    .concat(Array(5 - rows.length).fill("⬜"))
    .join("");
  const status = won ? "✅ Solved" : "❌ Missed";
  const line1 = specialty ? `DxDaily ${specialty} · ${date}` : `DxDaily ${date}`;
  const url = typeof window !== "undefined" ? window.location.origin : "";
  return `${line1}\n${grid}\n${status}  •  🔥 ${streak}${url ? `\n${url}` : ""}`;
};
